import { logger } from '@tuic/logger';
import { cache } from 'react';

import { Account } from '../../index';

import type { UpdateChecklistItem } from '../../generated/account';
import type { Locale } from '@tuic/i18n';

export const getChecklist = async (
  locale: Locale,
  idToken: string,
  bookingCode: string,
): Promise<Account.Checklist | null> => {
  const { data, error } = await Account.getChecklistData({
    path: {
      bookingCode,
    },
    query: {
      locale,
    },
    auth: idToken,
  });

  if (error) {
    const args = {
      locale,
      bookingCode,
      idToken,
    };

    logger.error(`Could not fetch checklist. ${JSON.stringify(args)}`);
    logger.error(error);

    return null;
  }

  return data ?? null;
};

export const memoizedGetChecklist = cache(getChecklist);

export const addChecklistItem = async (
  args: Account.AddChecklistItem & {
    idToken: string;
    bookingCode: string;
  },
): Promise<boolean> => {
  const { idToken, bookingCode, ...body } = args;

  const { data, error } = await Account.addChecklistItem({
    body,
    path: {
      bookingCode,
    },
    auth: idToken,
  });

  if (error) {
    logger.error(`Could not add item to checklist. ${JSON.stringify(args)}`);
    logger.error(error);

    return false;
  }

  return data?.success ?? false;
};

export const updateChecklistItem = async (
  args: UpdateChecklistItem & {
    idToken: string;
    bookingCode: string;
    checklistItemCode: string;
  },
): Promise<boolean> => {
  const { idToken, bookingCode, checklistItemCode, ...body } = args;

  const { data, error } = await Account.updateChecklistItem({
    body,
    path: {
      bookingCode,
      code: checklistItemCode,
    },
    auth: idToken,
  });

  if (error) {
    logger.error(`Could not update item in checklist. ${JSON.stringify(args)}`);
    logger.error(error);

    return false;
  }

  return data?.success ?? false;
};

export const deleteChecklistItem = async (args: {
  idToken: string;
  bookingCode: string;
  checklistItemCode: string;
  type: Account.UpdateChecklistItem['type'];
}): Promise<boolean> => {
  const { idToken, bookingCode, checklistItemCode, type } = args;

  const { data, error } = await Account.updateChecklistItem({
    body: {
      type,
      removed: true,
    },
    path: {
      bookingCode,
      code: checklistItemCode,
    },
    auth: idToken,
  });

  if (error) {
    logger.error(`Could not delete item in checklist. ${JSON.stringify(args)}`);
    logger.error(error);

    return false;
  }

  return data?.success ?? false;
};
